'use client';

import NextImage from 'next/image';
import type { ImageProps as NextImageProps } from 'next/image';

import { cn } from '@utils/cn';
import type { ImageStoryblok } from '@utils/storyblok/storyblok';

import { getFocalPoint } from './utils/getFocalPoint';
import { getImageLoader } from './utils/getImageLoader';
import { rgbDataURL } from './utils/rgbDataUrl';

const DEFAULT_IMAGE_SIZES = '(max-width: 992px) 50vw, 600px';

type ImageProps = Omit<ImageStoryblok, '_uid'> & {
  imageSizes?: string;
  priority?: boolean;
};

export function Image({
  aspectRatio,
  blur = true,
  className,
  asset,
  contain = false,
  isRound = false,
  imageSizes = DEFAULT_IMAGE_SIZES,
  hasDarkOverlay,
  priority = false,
}: ImageProps) {
  const src = asset.filename;

  const imageLoader = getImageLoader(src, aspectRatio, asset.focus);
  const focalPosition = getFocalPoint(src, asset.focus);

  const imageClasses = cn(
    'max-w-full',
    isRound && 'aspect-square w-full max-w-[440px]',
    contain ? 'object-contain' : 'object-cover',
    className,
  );

  const imageStyle: NextImageProps['style'] = {
    objectPosition: focalPosition || 'center',
  };

  if (blur) {
    return (
      <NextImage
        alt={asset.alt ?? ''}
        blurDataURL={rgbDataURL(234, 234, 234)}
        className={imageClasses}
        fill
        loader={imageLoader}
        placeholder="blur"
        priority={priority}
        sizes={imageSizes}
        src={src}
        style={imageStyle}
      />
    );
  }

  return (
    <NextImage
      alt={asset.alt ?? ''}
      className={imageClasses}
      fill
      loader={imageLoader}
      priority={priority}
      sizes={imageSizes}
      src={src}
      style={imageStyle}
    />
  );
}
